import {
  BssdkEventName,
  LanguageEventName,
  MONITORING_EVENT
} from './constants';
import {
  captureMonitoringException,
  reportMonitoringEvent
} from './client';
import { getDurationMs, normalizeCode, normalizeErrorType } from './sanitize';

export type TicketEmailApiName = 'email_submit' | 'faq_list' | 'email_guide' | 'logistics_create';
export type TicketEmailApiResult =
  | 'success'
  | 'biz_error'
  | 'network_error'
  | 'unexpected_error'
  | 'validation_error';
export type TicketEmailPage =
  | 'contact_us'
  | 'logistics_modification'
  | 'logistics_modification_new'
  | 'unknown';

type ApiResultInput = {
  apiName: TicketEmailApiName;
  result: TicketEmailApiResult;
  startedAt: number;
  code?: unknown;
  stage?: string;
  fallback?: 'local';
  priority?: 'low';
  page?: TicketEmailPage;
};

type DependencyResultInput = {
  dependency: 'starling' | 'email_language_tcc';
  stage: 'load' | 'fetch' | 'parse';
  result: 'success' | 'network_error' | 'unexpected_error';
  startedAt: number;
  error?: unknown;
  locale?: string;
  projectId?: number;
};

export const getTicketEmailPage = (pathname?: string): TicketEmailPage => {
  const currentPathname =
    pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname);

  if (currentPathname === '/contact-us/logistics-modification') {
    return 'logistics_modification';
  }
  if (currentPathname === '/contact-us/logistics-modification-new') {
    return 'logistics_modification_new';
  }
  if (currentPathname === '/contact-us') {
    return 'contact_us';
  }
  return 'unknown';
};

export const reportApiResult = ({
  apiName,
  result,
  startedAt,
  code,
  stage,
  fallback,
  priority,
  page = getTicketEmailPage()
}: ApiResultInput): void => {
  reportMonitoringEvent(
    MONITORING_EVENT.apiResult,
    {
      api_name: apiName,
      result,
      code: normalizeCode(code),
      stage,
      fallback,
      priority,
      page
    },
    {
      duration_ms: getDurationMs(startedAt)
    }
  );
};

export const reportDependencyResult = ({
  dependency,
  stage,
  result,
  startedAt,
  error,
  locale,
  projectId
}: DependencyResultInput): void => {
  reportMonitoringEvent(
    MONITORING_EVENT.dependencyResult,
    {
      dependency,
      stage,
      result,
      error_type: error === undefined ? undefined : normalizeErrorType(error),
      locale,
      project_id: projectId === undefined ? undefined : String(projectId)
    },
    {
      duration_ms: getDurationMs(startedAt)
    }
  );
};

export const reportBssdkEvent = (
  eventName: BssdkEventName,
  payload: Record<string, unknown> = {}
): void => {
  reportMonitoringEvent(eventName, {
    region: payload.region,
    is_boe: payload.is_boe,
    captcha_initialized: payload.captcha_initialized,
    captcha_status: payload.captcha_status,
    idc_name: payload.idc_name,
    error_type: payload.error === undefined ? undefined : normalizeErrorType(payload.error)
  });
};

export const reportLanguageEvent = (
  eventName: LanguageEventName,
  payload: { error?: unknown; locale?: string } = {}
): void => {
  reportMonitoringEvent(eventName, {
    error_type: payload.error === undefined ? undefined : normalizeErrorType(payload.error),
    locale: payload.locale
  });
};

export const reportRenderDegraded = ({
  error,
  hasRoot,
  rootChildCount,
  rootTextLength,
  page = getTicketEmailPage()
}: {
  error: unknown;
  hasRoot: boolean;
  rootChildCount: number;
  rootTextLength: number;
  page?: TicketEmailPage;
}): void => {
  reportMonitoringEvent(
    MONITORING_EVENT.blankScreenErrorBoundary,
    {
      error_type: normalizeErrorType(error),
      has_root: hasRoot,
      page
    },
    {
      root_child_count: rootChildCount,
      root_text_length: rootTextLength
    }
  );
};

export const captureBusinessException = (
  error: unknown,
  scene: 'email_submit' | 'error_boundary',
  page = getTicketEmailPage()
): void => {
  captureMonitoringException(error, {
    stage: scene,
    page
  });
};

